import { RouterModule, Routes } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import {
  LoginPageComponent,
  ResetPasswordPageComponent,
  SetNewPasswordPageComponent,
} from './authorization/components';
import { ProfilePageComponent } from './pages/my-profile/containers';
import { PartnerDetailComponent, PartnersPageComponent } from './pages/companies/containers';
import {
  PartnershipsPageComponent,
  PartnershipDetailComponent,
} from './pages/partnerships/containers';
import {
  InstallationGroupsPageComponent,
  InstallationGroupDetailComponent,
} from './pages/installation-groups/containers';
import { LocationDetailComponent, LocationsPageComponent } from './pages/locations/containers';
import { DeviceDetailComponent, DevicesPageComponent } from './pages/devices/containers';
import {
  InstallationDetailComponent,
  InstallationsPageComponent,
  MigrationReportPageComponent,
} from './pages/installations/containers';
import { ServiceDetailPageComponent, ServicesPageComponent } from './pages/services/containers';
import { DataExportPageComponent } from './pages/data-export/containers';
import { SettingsPageComponent } from './pages/settings/containers';
import { SupportPageComponent } from './pages/support/containers';
import { DocumentationPageComponent } from './pages/documentation/containers';
import { SearchPageComponent } from './pages/search/containers';
import { APP_PATHS } from '@core/constants';
import { featureGuard } from './authorization/feature.guard';
import { authCallbackGuard, authGuard, loginGuard, xemexGuard } from './authorization/auth.guard';
import { MyCompanyPageComponent } from './pages/my-company/containers';
import { JoinNetworkPageComponent } from './authorization/components/join-network-page/join-network-page.component';
import { ResetPasswordSuccessPageComponent } from './authorization/components/reset-password-success-page/reset-password-success-page.component';
import { SetNewPasswordSuccessPageComponent } from './authorization/components/set-new-password-success-page/set-new-password-success-page.component';
import { LinkExpiredPageComponent } from './authorization/components/link-expired-page/link-expired-page.component';
import { UserDetailsPageComponent } from './pages/my-profile/containers/user-details-page/user-details-page.component';
import { NgModule } from '@angular/core';
import { AlarmsPageComponent } from './pages/alarms/containers';
import { DashboardPageComponent } from './pages/dashboard/containers';
import { FirmwareManagementPageComponent } from './pages/firmware-management/containers';
import { ComponentLibraryPageComponent } from './pages/component-library/component-library-page.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: APP_PATHS.Dashboard,
    pathMatch: 'full',
  },
  {
    // MSAL (navigateToLoginRequestUrl=false) lands here after Azure. The guard owns the
    // post-SSO destination as a single navigation (resolves redirect_uri or default),
    // so it must NOT redirectTo '/' — that default chain raced and dropped the deep link.
    // Component-less: the guard always returns a UrlTree (or false), so nothing renders.
    matcher: url =>
      url.length === 1 &&
      (url[0].path.startsWith('code') || url[0].path.startsWith('auth-callback'))
        ? { consumed: url }
        : null,
    canActivate: [authCallbackGuard],
    children: [],
  },
  {
    path: APP_PATHS.Auth,
    children: [
      {
        path: APP_PATHS.Login,
        component: LoginPageComponent,
        canActivate: [loginGuard],
      },
      { path: APP_PATHS.ResetPassword, component: ResetPasswordPageComponent },
      {
        path: APP_PATHS.SetNewPassword,
        component: SetNewPasswordPageComponent,
      },
      {
        path: APP_PATHS.JoinNetwork,
        component: JoinNetworkPageComponent,
      },
      {
        path: APP_PATHS.ResetPasswordSuccess,
        component: ResetPasswordSuccessPageComponent,
      },
      {
        path: APP_PATHS.SetNewPasswordSuccess,
        component: SetNewPasswordSuccessPageComponent,
      },
      {
        path: APP_PATHS.LinkExpired,
        component: LinkExpiredPageComponent,
      },
    ],
  },
  {
    path: '',
    component: PagesComponent,
    canActivate: [authGuard],
    children: [
      {
        path: APP_PATHS.Dashboard,
        component: DashboardPageComponent,
      },
      {
        path: APP_PATHS.Profile,
        component: ProfilePageComponent,
      },
      {
        path: APP_PATHS.User,
        component: UserDetailsPageComponent,
      },
      {
        path: APP_PATHS.Partners,
        component: PartnersPageComponent,
      },
      {
        path: APP_PATHS.PartnerDetail,
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: PartnerDetailComponent,
          },
        ],
      },
      {
        path: APP_PATHS.Partnerships,
        component: PartnershipsPageComponent,
        canActivate: [featureGuard('view_partnerships')],
      },
      {
        path: APP_PATHS.PartnershipDetail,
        component: PartnershipDetailComponent,
        canActivate: [featureGuard('view_partnerships')],
      },
      {
        path: APP_PATHS.Installations,
        component: InstallationsPageComponent,
        canActivate: [xemexGuard, featureGuard('view_installations')],
      },
      {
        path: APP_PATHS.MigrationReport,
        component: MigrationReportPageComponent,
        canActivate: [xemexGuard, featureGuard('view_migration_report')],
      },
      {
        path: APP_PATHS.InstallationDetail,
        canActivate: [xemexGuard, featureGuard('view_installations')],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          // The data table now sits on the insights tab; old links to it land there.
          {
            path: 'data',
            redirectTo: 'insights',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: InstallationDetailComponent,
          },
        ],
      },
      {
        path: APP_PATHS.InstallationGroups,
        component: InstallationGroupsPageComponent,
      },
      {
        path: APP_PATHS.InstallationGroupDetail,
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: InstallationGroupDetailComponent,
          },
        ],
      },
      {
        path: APP_PATHS.Services,
        component: ServicesPageComponent,
      },
      {
        path: APP_PATHS.ServiceDetail,
        component: ServiceDetailPageComponent,
      },
      {
        path: APP_PATHS.Locations,
        component: LocationsPageComponent,
      },
      {
        path: APP_PATHS.LocationDetail,
        component: LocationDetailComponent,
      },
      {
        path: APP_PATHS.Devices,
        component: DevicesPageComponent,
      },
      {
        path: APP_PATHS.DeviceDetail,
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: DeviceDetailComponent,
          },
        ],
      },
      {
        path: APP_PATHS.DataExport,
        component: DataExportPageComponent,
        canActivate: [featureGuard('export_device_data')],
      },
      {
        path: APP_PATHS.MyCompany,
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: MyCompanyPageComponent,
          },
        ],
      },
      {
        path: APP_PATHS.Settings,
        children: [
          {
            path: '',
            redirectTo: 'shipment-files',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: SettingsPageComponent,
          },
        ],
      },
      {
        path: APP_PATHS.Support,
        component: SupportPageComponent,
      },
      {
        path: APP_PATHS.Documentation,
        component: DocumentationPageComponent,
      },
      {
        path: APP_PATHS.Search,
        component: SearchPageComponent,
      },
      {
        path: APP_PATHS.Alarms,
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: ':tab',
            component: AlarmsPageComponent,
          },
        ],
      },
      {
        path: APP_PATHS.FirmwareManagement,
        component: FirmwareManagementPageComponent,
        canActivate: [xemexGuard],
      },
      {
        path: APP_PATHS.ComponentLibrary,
        component: ComponentLibraryPageComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { useHash: false, paramsInheritanceStrategy: 'emptyOnly' }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
